import { Controller } from "@hotwired/stimulus"
import { createChart, IChartApi, ISeriesApi, AreaSeries, LineData, Time } from "lightweight-charts"
import tailwindColors from "@/lib/tailwindColors"

interface TrendData {
  direction: "up" | "down" | "flat"
  value: number
  percent: number
  favorable_direction?: "up" | "down"
}

interface DataPoint {
  time: string // YYYY-MM-DD
  value: number
  currency?: string
  trend: TrendData
}

interface DataValue {
  values: DataPoint[]
  trend: {
    direction: "up" | "down" | "flat"
    favorable_direction: "up" | "down"
  }
  favorable_direction: "up" | "down"
}

export default class extends Controller {
  static values = {
    data: Object,
  }

  declare readonly dataValue: DataValue

  private chart: IChartApi | null = null
  private areaSeries: ISeriesApi<"Area"> | null = null
  private tooltip: HTMLDivElement | null = null
  private resizeObserver: ResizeObserver | null = null

  connect() {
    this.install()
    document.addEventListener("turbo:load", this.reinstall)
  }

  disconnect() {
    this.teardown()
    document.removeEventListener("turbo:load", this.reinstall)
  }

  private reinstall = () => {
    this.teardown()
    this.install()
  }

  private teardown() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.chart?.remove()
    this.chart = null
    this.areaSeries = null
    this.tooltip?.remove()
    this.tooltip = null
  }

  private install() {
    const values = this.dataValue?.values
    if (!Array.isArray(values) || values.length < 2) {
      this.drawEmpty()
      return
    }

    this.createChart()
    this.createTooltip()
    this.setupResizeObserver()
  }

  private drawEmpty() {
    const container = this.element as HTMLElement
    const width = container.clientWidth
    const height = container.clientHeight || 200

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    svg.setAttribute("width", String(width))
    svg.setAttribute("height", String(height))
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`)

    const centerX = width / 2
    const centerY = height / 2

    // Vertical dashed line
    const line = document.createElementNS("http://www.w3.org/2000/svg", "line")
    line.setAttribute("x1", String(centerX))
    line.setAttribute("y1", "0")
    line.setAttribute("x2", String(centerX))
    line.setAttribute("y2", String(height))
    line.setAttribute("stroke", tailwindColors.gray[300])
    line.setAttribute("stroke-dasharray", "4, 4")
    svg.appendChild(line)

    // Center dot
    const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle")
    circle.setAttribute("cx", String(centerX))
    circle.setAttribute("cy", String(centerY))
    circle.setAttribute("r", "4")
    circle.setAttribute("fill", tailwindColors.gray[400])
    svg.appendChild(circle)

    container.appendChild(svg)
  }

  private createChart() {
    const container = this.element as HTMLElement
    const trendColor = this.getTrendColor()

    this.chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight || 200,
      layout: {
        background: { color: "transparent" },
        textColor: tailwindColors.gray[500],
        fontFamily: "Inter, sans-serif",
        attributionLogo: false, // Attribution provided at /licenses
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      rightPriceScale: {
        visible: false,
      },
      timeScale: {
        visible: true,
        borderVisible: false,
        fixLeftEdge: true,
        fixRightEdge: true,
        timeVisible: false,
      },
      crosshair: {
        vertLine: {
          color: tailwindColors.gray[300],
          style: 1, // Dashed
          width: 1,
          labelVisible: false,
        },
        horzLine: {
          visible: false,
          labelVisible: false,
        },
      },
      handleScale: false,
      handleScroll: false,
    })

    // Create area series with gradient
    this.areaSeries = this.chart.addSeries(AreaSeries, {
      lineColor: trendColor,
      lineWidth: 2,
      topColor: this.hexToRgba(trendColor, 0.06),
      bottomColor: this.hexToRgba(trendColor, 0),
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 4,
      crosshairMarkerBackgroundColor: trendColor,
      crosshairMarkerBorderColor: trendColor,
      priceLineVisible: false,
      lastValueVisible: false,
    })

    // Convert data to Lightweight Charts format
    const chartData: LineData<Time>[] = this.dataValue.values.map((d) => ({
      time: d.time as Time,
      value: d.value,
    }))

    this.areaSeries.setData(chartData)

    // Fit content and set time scale range
    this.chart.timeScale().fitContent()

    // Add x-axis labels (start and end dates)
    this.addDateLabels()
  }

  private addDateLabels() {
    const values = this.dataValue.values
    if (values.length < 2) return

    const container = this.element as HTMLElement
    const labelContainer = document.createElement("div")
    labelContainer.className = "flex justify-between px-0 mt-1"
    labelContainer.style.color = tailwindColors.gray[500]
    labelContainer.style.fontSize = "12px"
    labelContainer.style.fontWeight = "500"

    const startDate = this.formatDateLabel(values[0].time)
    const endDate = this.formatDateLabel(values[values.length - 1].time)

    const startLabel = document.createElement("span")
    startLabel.textContent = startDate
    const endLabel = document.createElement("span")
    endLabel.textContent = endDate

    labelContainer.appendChild(startLabel)
    labelContainer.appendChild(endLabel)

    container.appendChild(labelContainer)
  }

  private formatDateLabel(dateStr: string): string {
    const date = new Date(dateStr + "T00:00:00")
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }

  private createTooltip() {
    const container = this.element as HTMLElement
    container.style.position = "relative"

    this.tooltip = document.createElement("div")
    this.tooltip.style.cssText = `
      position: absolute;
      padding: 8px;
      font: 14px Inter, sans-serif;
      background: ${tailwindColors.white};
      border: 1px solid ${tailwindColors["alpha-black"][100]};
      border-radius: 10px;
      pointer-events: none;
      opacity: 0;
      z-index: 999;
      transition: opacity 0.1s;
    `
    container.appendChild(this.tooltip)

    this.chart?.subscribeCrosshairMove((param) => {
      if (!param.time || !param.point || !this.tooltip) {
        if (this.tooltip) this.tooltip.style.opacity = "0"
        return
      }

      const dataPoint = this.findDataPoint(param.time as string)
      if (!dataPoint) {
        this.tooltip.style.opacity = "0"
        return
      }

      this.tooltip.innerHTML = this.tooltipTemplate(dataPoint)
      this.tooltip.style.opacity = "1"

      // Position tooltip
      const containerRect = container.getBoundingClientRect()
      const tooltipWidth = 250
      let left = param.point.x + 10

      // Prevent overflow on right
      if (left + tooltipWidth > containerRect.width) {
        left = param.point.x - tooltipWidth - 10
      }

      this.tooltip.style.left = `${left}px`
      this.tooltip.style.top = `${param.point.y - 10}px`
    })
  }

  private findDataPoint(time: string): DataPoint | undefined {
    return this.dataValue.values.find((d) => d.time === time)
  }

  private tooltipTemplate(datum: DataPoint): string {
    const trendColor = this.tooltipTrendColor(datum)
    const isZeroTrend = datum.trend.value === 0

    const formattedDate = new Date(datum.time + "T00:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    })

    const formattedValue = this.formatCurrency(datum.value, datum.currency)
    const formattedChange = this.formatChange(datum.trend.value, datum.currency)

    return `
      <div style="margin-bottom: 4px; color: ${tailwindColors.gray[500]};">
        ${formattedDate}
      </div>
      <div style="display: flex; align-items: center; gap: 16px;">
        <div style="display: flex; align-items: center; gap: 8px;">
          <svg width="10" height="10">
            <circle cx="5" cy="5" r="4" stroke="${trendColor}" fill="transparent" stroke-width="1"></circle>
          </svg>
          ${formattedValue}
        </div>
        ${
          isZeroTrend
            ? '<span style="width: 80px;"></span>'
            : `<span style="color: ${trendColor};">
            ${formattedChange} (${datum.trend.percent}%)
          </span>`
        }
      </div>
    `
  }

  private formatCurrency(value: number, currency?: string): string {
    if (!currency) return String(value)
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency,
    }).format(value)
  }

  private formatChange(value: number, currency?: string): string {
    return new Intl.NumberFormat(undefined, {
      style: currency ? "currency" : "decimal",
      currency: currency || undefined,
      signDisplay: "always",
    }).format(value)
  }

  private tooltipTrendColor(datum: DataPoint): string {
    return {
      up: tailwindColors.success,
      down: tailwindColors.error,
      flat: tailwindColors.gray[500],
    }[datum.trend.direction]
  }

  private setupResizeObserver() {
    const container = this.element as HTMLElement
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        if (this.chart && width > 0 && height > 0) {
          this.chart.applyOptions({ width, height: height - 30 }) // Account for date labels
        }
      }
    })
    this.resizeObserver.observe(container)
  }

  private getTrendColor(): string {
    const { direction, favorable_direction } = this.dataValue.trend
    if (direction === "flat") return tailwindColors.gray[500]
    return direction === favorable_direction ? tailwindColors.green[500] : tailwindColors.error
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
}
